import { useEffect, useRef, useState } from 'react';
import type { PlayerEngine, PlayableTrack } from '@karozik/music/player';
import type { PublicTrack } from '@karozik/shared';
import type { PartyConnection } from '@karozik/ui';

export interface PlayerHealth {
  offline: boolean;
  offlinePlayed: number;
  lastError: string | null;
}

/**
 * Drives the audio engine from the server state.
 *
 * - The server says WHAT plays (`nowPlaying`); the player says WHEN it ends —
 *   a little before the real end, so the next track crossfades in.
 * - If the server is unreachable when a track ends, the player keeps going on
 *   its own with the last known queue + the fallback list, and reports what it
 *   played once the connection is back.
 */
export function usePlayerAudio(conn: PartyConnection, engine: PlayerEngine | null, started: boolean, duck: boolean): PlayerHealth {
  const { state, socket, connected, fallback } = conn;
  const currentItem = useRef<string | null>(null);
  const endRequestedFor = useRef<string | null>(null);
  const offlineLog = useRef<{ played: { trackId: string; startedAt: number }[]; current: { trackId: string; startedAt: number } | null }>({
    played: [],
    current: null,
  });
  const localQueue = useRef<PublicTrack[]>([]);
  const playedIds = useRef(new Set<string>());
  const [health, setHealth] = useState<PlayerHealth>({ offline: false, offlinePlayed: 0, lastError: null });
  const stateRef = useRef(state);
  stateRef.current = state;
  const connectedRef = useRef(connected);
  connectedRef.current = connected;

  const fadeMs = state?.staff?.fadeMs ?? 4000;
  const ended = state?.event.status === 'ended';
  const isEnded = () => stateRef.current?.event.status === 'ended';

  // Remember what could be played without the server.
  useEffect(() => {
    if (!state) return;
    const list = [...state.queue.map((q) => q.track), ...(fallback?.tracks ?? [])];
    const seen = new Set<string>();
    localQueue.current = list.filter((t) => t.previewUrl !== undefined && !seen.has(t.id) && (seen.add(t.id), true));
  }, [state, fallback]);

  /** Reports autonomous playback to the server (when reachable). */
  const resync = useRef<() => void>(() => undefined);
  resync.current = () => {
    const log = offlineLog.current;
    if (!socket || !connectedRef.current || (log.played.length === 0 && !log.current)) return;
    socket.emit('player:resync', { played: log.played, current: log.current });
    offlineLog.current = { played: [], current: null };
    setHealth((h) => ({ ...h, offline: false, offlinePlayed: 0 }));
  };

  const playLocalNext = useRef<() => void>(() => undefined);
  playLocalNext.current = () => {
    if (!engine || isEnded()) return;
    const log = offlineLog.current;
    if (log.current) log.played.push(log.current);
    else if (stateRef.current?.nowPlaying) log.played.push({ trackId: stateRef.current.nowPlaying.track.id, startedAt: stateRef.current.nowPlaying.startedAt });
    const next = localQueue.current.find((t) => !playedIds.current.has(t.id) && (engine.kind === 'musickit' || t.previewUrl));
    if (!next) return;
    playedIds.current.add(next.id);
    log.current = { trackId: next.id, startedAt: Date.now() };
    currentItem.current = `offline:${next.id}`;
    void engine.play(toPlayable(next), { fadeMs });
    setHealth((h) => ({ ...h, offline: true, offlinePlayed: log.played.length }));
    resync.current();
  };

  // Back online: tell the server what happened meanwhile.
  useEffect(() => {
    if (connected) resync.current();
  }, [connected, socket]);

  // Follow the server: blind test clips, then the current track.
  const np = state?.nowPlaying ?? null;
  const bt = state?.blindtest ?? null;
  const clip = bt?.stage === 'playing' ? bt.clip : null;
  useEffect(() => {
    if (!engine || !started || !state || ended) return;
    if (clip) {
      if (engine.currentTrackId() !== clip.trackId) {
        currentItem.current = `clip:${clip.trackId}`;
        void engine.play({ id: clip.trackId, title: '', artist: '', durationMs: clip.durationMs, previewUrl: clip.previewUrl }, { offsetMs: clip.offsetMs, fadeMs: 800 });
      }
      return;
    }
    if (bt) return; // intro / reveal / final: keep what is playing (the reveal lets the clip ring on).
    if (!np || np.id === currentItem.current) return;
    playedIds.current.add(np.track.id);
    // Same track already audible (reconnection, resync): adopt it, don't restart it.
    if (engine.currentTrackId() === np.track.id && engine.isPlaying()) {
      currentItem.current = np.id;
      return;
    }
    const firstLoad = currentItem.current === null;
    currentItem.current = np.id;
    const elapsed = Date.now() + conn.clockSkew - np.startedAt;
    const offsetMs = firstLoad && engine.kind === 'musickit' && elapsed > 5000 && elapsed < np.track.durationMs - 20_000 ? elapsed : 0;
    void engine.play(toPlayable(np.track), { fadeMs, offsetMs });
  }, [engine, started, np?.id, clip?.trackId, !!bt, ended]); // eslint-disable-line react-hooks/exhaustive-deps

  // Party closed: the music fades out and nothing starts again on its own.
  useEffect(() => {
    if (!engine || !started || !ended) return;
    currentItem.current = null;
    void engine.stop(3000);
  }, [engine, started, ended]);

  // Pause / resume.
  useEffect(() => {
    if (!engine || !started || isEnded()) return;
    if (state?.paused) engine.pause();
    else engine.resume();
  }, [engine, started, state?.paused]);

  // Volume: master × ducking (mic, photo) × blind test intro/final.
  const level = state?.staff?.ducking.level ?? 0.15;
  const volume = state?.volume ?? 0.8;
  const quietForGame = bt && (bt.stage === 'intro' || bt.stage === 'final');
  useEffect(() => {
    if (!engine) return;
    engine.setVolume(volume * (duck ? level : 1) * (quietForGame ? 0.35 : 1), duck ? 400 : 900);
  }, [engine, volume, duck, level, quietForGame]);

  // End of track (natural, or a little early for the crossfade), errors.
  useEffect(() => {
    if (!engine || !socket) return;
    const requestNext = () => {
      const item = currentItem.current;
      if (!item || endRequestedFor.current === item || stateRef.current?.blindtest || isEnded()) return;
      endRequestedFor.current = item;
      if (connectedRef.current && !item.startsWith('offline:') && !item.startsWith('clip:')) {
        socket.emit('player:ended', { itemId: item });
        // Watchdog: never stay silent if the server does not answer in time.
        setTimeout(() => {
          if (currentItem.current === item && !stateRef.current?.blindtest && !engine.isPlaying()) playLocalNext.current();
        }, 4000);
      } else playLocalNext.current();
    };
    const offEnded = engine.on('ended', requestNext);
    const offError = engine.on('error', ({ message }) => {
      setHealth((h) => ({ ...h, lastError: message }));
      const item = currentItem.current;
      if (connectedRef.current && item && !item.includes(':')) socket.emit('player:error', { itemId: item, message });
      else playLocalNext.current();
    });
    const id = setInterval(() => {
      const d = engine.durationMs();
      if (d > 0 && engine.isPlaying() && d - engine.positionMs() < fadeMs + 300) requestNext();
    }, 400);
    return () => {
      offEnded();
      offError();
      clearInterval(id);
    };
  }, [engine, socket, fadeMs]);

  return health;
}

function toPlayable(t: PublicTrack): PlayableTrack {
  return { id: t.id, title: t.title, artist: t.artist, durationMs: t.durationMs, previewUrl: t.previewUrl ?? null };
}
